import {
  Children,
  ComponentProps,
  createContext,
  isValidElement,
  type ReactNode,
  type Ref,
  use,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';

import { useTabsIndicator, useTabsOverflow } from '@/hooks';

const tabsListVariants = cva('group/tabs-list text-muted-foreground', {
  variants: {
    variant: {
      line: 'scrollbar-none relative flex w-full items-end gap-0.5 overflow-x-auto border-b border-border',
      segmented:
        'bg-muted inline-flex w-fit items-center gap-0.5 rounded-md p-0.5',
    },
  },
  defaultVariants: { variant: 'line' },
});

const TABS_TRIGGER_BASE_CLASS_NAME =
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap text-sm font-medium transition-colors outline-none disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring data-[overflow=true]:hidden [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

const TABS_TRIGGER_LINE_CLASS_NAME =
  'group-data-[variant=line]/tabs-list:h-9 group-data-[variant=line]/tabs-list:rounded-t-md group-data-[variant=line]/tabs-list:px-2.5 group-data-[variant=line]/tabs-list:hover:bg-muted/70 group-data-[variant=line]/tabs-list:hover:text-foreground group-data-[variant=line]/tabs-list:data-[state=active]:text-link group-data-[variant=line]/tabs-list:after:absolute group-data-[variant=line]/tabs-list:after:inset-x-2 group-data-[variant=line]/tabs-list:after:-bottom-px group-data-[variant=line]/tabs-list:after:h-0.5 group-data-[variant=line]/tabs-list:after:rounded-full group-data-[variant=line]/tabs-list:data-[state=active]:after:bg-primary group-data-[indicator=true]/tabs-list:after:hidden';

const TABS_TRIGGER_SEGMENTED_CLASS_NAME =
  'group-data-[variant=segmented]/tabs-list:h-7 group-data-[variant=segmented]/tabs-list:rounded-sm group-data-[variant=segmented]/tabs-list:px-2.5 group-data-[variant=segmented]/tabs-list:hover:text-foreground group-data-[variant=segmented]/tabs-list:data-[state=active]:bg-card group-data-[variant=segmented]/tabs-list:data-[state=active]:text-foreground group-data-[variant=segmented]/tabs-list:data-[state=active]:shadow-xs';

const TABS_TRIGGER_CLASS_NAME = [
  TABS_TRIGGER_BASE_CLASS_NAME,
  TABS_TRIGGER_LINE_CLASS_NAME,
  TABS_TRIGGER_SEGMENTED_CLASS_NAME,
].join(' ');

type TabsContextValue = {
  value: string | undefined;
  onValueChange: (value: string) => void;
};

const TabsContext = createContext<TabsContextValue | null>(null);

const Tabs = (props: ComponentProps<typeof TabsPrimitive.Root>) => {
  const {
    className,
    value: propValue,
    defaultValue,
    onValueChange: propOnValueChange,
    ...restProps
  } = props;
  const [innerValue, setInnerValue] = useState(defaultValue);
  const value = propValue ?? innerValue;

  const onValueChange = (nextValue: string) => {
    if (propValue === undefined) {
      setInnerValue(nextValue);
    }
    propOnValueChange?.(nextValue);
  };

  return (
    <TabsContext value={{ value, onValueChange }}>
      <TabsPrimitive.Root
        data-slot="tabs"
        value={value}
        onValueChange={onValueChange}
        className={cn('flex flex-col gap-3', className)}
        {...restProps}
      />
    </TabsContext>
  );
};

const getTriggerLabel = (children: ReactNode, value: string) => {
  const trigger = Children.toArray(children).find(
    (child) =>
      isValidElement<{ value?: unknown; children?: ReactNode }>(child) &&
      child.props.value === value,
  );

  return isValidElement<{ children?: ReactNode }>(trigger)
    ? trigger.props.children
    : value;
};

type TabsOverflowMenuProps = {
  ref: Ref<HTMLButtonElement>;
  hiddenValues: string[];
  activeValue: string | undefined;
  isActive: boolean;
  onValueChange: (value: string) => void;
  children: ReactNode;
};

const TabsOverflowMenu = (props: TabsOverflowMenuProps) => {
  const { ref, hiddenValues, activeValue, isActive, onValueChange, children } =
    props;
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          ref={ref}
          type="button"
          data-state={isActive ? 'active' : 'inactive'}
          className={cn(
            TABS_TRIGGER_CLASS_NAME,
            hiddenValues.length === 0 && 'invisible absolute',
          )}
        >
          {t('More')}
          <Badge variant="neutral">{hiddenValues.length || 1}</Badge>
          <ChevronDown className="transition-transform duration-200 in-aria-expanded:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {hiddenValues.map((hiddenValue) => (
          <DropdownMenuItem
            key={hiddenValue}
            data-active={hiddenValue === activeValue}
            className="data-[active=true]:text-link"
            onSelect={() => onValueChange(hiddenValue)}
          >
            {getTriggerLabel(children, hiddenValue)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const TabsList = (
  props: ComponentProps<typeof TabsPrimitive.List> &
    VariantProps<typeof tabsListVariants>,
) => {
  const { className, variant = 'line', children, ...restProps } = props;
  const tabs = use(TabsContext);
  const listRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const isLine = variant === 'line';
  const activeValue = tabs?.value;
  const childrenKey = Children.toArray(children)
    .filter(isValidElement<{ value?: unknown }>)
    .map((child) => String(child.props.value))
    .join('|');
  const hiddenValues = useTabsOverflow({
    listRef,
    moreRef,
    isEnabled: isLine,
    childrenKey,
  });
  const isMoreActive =
    activeValue !== undefined && hiddenValues.includes(activeValue);
  useTabsIndicator({
    listRef,
    moreRef,
    indicatorRef,
    isEnabled: isLine,
    activeValue,
    isMoreActive,
    childrenKey,
  });

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      data-variant={variant}
      data-indicator={isLine && !!tabs}
      className={cn(tabsListVariants({ variant }), className)}
      {...restProps}
    >
      {children}
      {isLine && (
        <TabsOverflowMenu
          ref={moreRef}
          hiddenValues={hiddenValues}
          activeValue={activeValue}
          isActive={isMoreActive}
          onValueChange={(value) => tabs?.onValueChange(value)}
        >
          {children}
        </TabsOverflowMenu>
      )}
      {isLine && !!tabs && (
        <span
          ref={indicatorRef}
          aria-hidden="true"
          className="bg-primary pointer-events-none absolute -bottom-px left-0 h-0.5 rounded-full opacity-0 data-[ready=true]:transition-[transform,width] data-[ready=true]:duration-200"
        />
      )}
    </TabsPrimitive.List>
  );
};

const TabsTrigger = (props: ComponentProps<typeof TabsPrimitive.Trigger>) => {
  const { className, value, ...restProps } = props;

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      data-value={value}
      value={value}
      className={cn(TABS_TRIGGER_CLASS_NAME, className)}
      {...restProps}
    />
  );
};

const TabsContent = (props: ComponentProps<typeof TabsPrimitive.Content>) => {
  const { className, ...restProps } = props;

  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...restProps}
    />
  );
};

export {
  Tabs,
  TABS_TRIGGER_CLASS_NAME,
  TabsContent,
  TabsList,
  tabsListVariants,
  TabsTrigger,
};
